import {
  differenceInDays,
  differenceInYears,
  eachQuarterOfInterval,
  format,
  isBefore,
  parse,
} from 'date-fns';
import { dateToUTCMidnight, fromDateString, toUTCMidnight } from '@/lib/utils/common/dateFormats';

export interface NormalizedPlannedTimelineDates {
  hasAnyDate: boolean;
  start: string | undefined;
  finish: string | undefined;
}

export function getNormalizePlannedDates(
  plannedStartDateTime: string | null | undefined,
  plannedFinishDateTime: string | null | undefined,
): NormalizedPlannedTimelineDates {
  const start = plannedStartDateTime?.split('T')[0];
  const finish = plannedFinishDateTime?.split('T')[0];
  const hasAnyDate = !!(start || finish);
  const isInverted = start && finish && isBefore(finish, start);
  return {
    hasAnyDate,
    start: isInverted ? start : (start ?? finish),
    finish: isInverted ? start : (finish ?? start),
  };
}

export const getMonthName = (date: Date) => {
  const { locale } = Intl.DateTimeFormat().resolvedOptions();
  return new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC', calendar: 'gregory' })
    .format(date)
    .toLocaleUpperCase(locale);
};

export const getQuarter = (date: Date): number => {
  return Math.floor(date.getUTCMonth() / 3) + 1;
};

export const getQuarterLabel = (quarter: number, year: number): string => {
  return `Q${quarter} ${year}`;
};

const MAX_YEARS_FOR_MONTH_VIEW = 1;

export const shouldUseQuarterView = (from: string, to: string): boolean => {
  const fromDate = new Date(from);
  const toDate = new Date(to);
  const diffInYears = differenceInYears(toDate, fromDate);
  return diffInYears >= MAX_YEARS_FOR_MONTH_VIEW;
};

export type TimelineGranularity = 'month' | 'quarter' | 'year';

export const MONTH_VIEW_MIN_DAY_WIDTH = 12;
export const QUARTER_VIEW_MIN_DAY_WIDTH = 3;

export const granularityForDayWidth = (dayWidth: number): TimelineGranularity => {
  if (dayWidth >= MONTH_VIEW_MIN_DAY_WIDTH) return 'month';
  if (dayWidth >= QUARTER_VIEW_MIN_DAY_WIDTH) return 'quarter';
  return 'year';
};

type TwoWeekRange = { start: Date; end: Date };

const BIWEEK_STRIDE_DAYS = 14;
const BIWEEK_SPAN_DAYS = 13;

const addUtcDays = (date: Date, days: number): Date =>
  toUTCMidnight(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days);

const collectForwardBiweekRanges = ({
  fromDate,
  toDate,
  monthStart,
  monthEnd,
}: {
  fromDate: Date;
  toDate: Date;
  monthStart: Date;
  monthEnd: Date;
}): TwoWeekRange[] => {
  const ranges: TwoWeekRange[] = [];
  let currentStart = fromDate;

  while (currentStart <= toDate) {
    const currentEnd = addUtcDays(currentStart, BIWEEK_SPAN_DAYS);
    const rangeEnd = currentEnd > toDate ? toDate : currentEnd;

    if (currentStart >= monthStart && currentStart <= monthEnd) {
      ranges.push({ start: currentStart, end: rangeEnd });
    }

    currentStart = addUtcDays(currentStart, BIWEEK_STRIDE_DAYS);
  }

  return ranges;
};

const collectBacktrackedBiweekRanges = ({
  fromDate,
  monthStart,
  monthEnd,
}: {
  fromDate: Date;
  monthStart: Date;
  monthEnd: Date;
}): TwoWeekRange[] => {
  const ranges: TwoWeekRange[] = [];
  const dayBeforeFrom = addUtcDays(fromDate, -1);
  let backtrackStart = addUtcDays(fromDate, -BIWEEK_STRIDE_DAYS);

  while (backtrackStart >= monthStart) {
    const backtrackEnd = addUtcDays(backtrackStart, BIWEEK_SPAN_DAYS);

    if (backtrackEnd >= monthStart && backtrackEnd <= monthEnd) {
      ranges.unshift({
        start: backtrackStart,
        end: backtrackEnd < dayBeforeFrom ? backtrackEnd : dayBeforeFrom,
      });
    } else if (backtrackEnd > monthEnd) {
      ranges.unshift({
        start: backtrackStart,
        end: dayBeforeFrom < monthEnd ? dayBeforeFrom : monthEnd,
      });
      break;
    }

    backtrackStart = addUtcDays(backtrackStart, -BIWEEK_STRIDE_DAYS);
  }

  return ranges;
};

export const getTwoWeekRangesInMonth = ({
  year,
  month,
  from,
  to,
}: {
  year: number;
  month: number;
  from: string;
  to: string;
}): TwoWeekRange[] => {
  const fromDate = dateToUTCMidnight(parse(from, 'yyyy-MM-dd', new Date()));
  const toDate = dateToUTCMidnight(parse(to, 'yyyy-MM-dd', new Date()));

  const monthStart = toUTCMidnight(year, month, 1);
  const monthEnd = toUTCMidnight(year, month + 1, 0);

  const forwardRanges = collectForwardBiweekRanges({ fromDate, toDate, monthStart, monthEnd });
  if (forwardRanges.length > 0) return forwardRanges;

  if (monthEnd < fromDate) {
    return collectBacktrackedBiweekRanges({ fromDate, monthStart, monthEnd });
  }

  return forwardRanges;
};

export type TimelineDateAxisMonth = {
  start: Date;
  end: Date;
  /** ISO date string (yyyy-MM-dd) of `start`. Used as the bucket key for items. */
  key: string;
  /** Calendar year of this slice's start date. */
  year: number;
  /** Calendar month index (0-11) of this slice's start date. */
  monthIndex: number;
  /** Sequential index within the parent quarter when useQuarterView. */
  indexInQuarter: number;
};

export type TimelineDateAxisQuarter = {
  year: number;
  quarter: number;
  quarterLabel: string;
  months: TimelineDateAxisMonth[];
};

export type TimelineDateAxisQuarterSlice = {
  start: Date;
  end: Date;
  /** ISO date string (yyyy-MM-dd) of `start`. Used as the bucket key for items. */
  key: string;
  year: number;
  quarter: number;
  quarterLabel: string;
  /** Sequential index within the parent year. */
  indexInYear: number;
};

export type TimelineDateAxisYear = {
  year: number;
  quarters: TimelineDateAxisQuarterSlice[];
};

export type TimelineDateAxisBiWeek = {
  start: Date;
  end: Date;
  /** ISO date string (yyyy-MM-dd) of `start`. Used as the bucket key for items. */
  key: string;
  /** Sequential index within the parent calendar month. */
  index: number;
};

export type TimelineDateAxisMonthGroup = {
  year: number;
  monthIndex: number;
  monthName: string;
  /** Calendar-month boundaries (clamped to scope). */
  visibleMonthStart: Date;
  visibleMonthEnd: Date;
  biWeeks: TimelineDateAxisBiWeek[];
  /** True when there are no bi-weeks intersecting the scope; column renderers
   *  fall back to a single empty cell spanning the visible month. */
  isEmptyState: boolean;
};

export type TimelineDateAxis =
  | {
      view: 'quarter';
      useQuarterView: true;
      useYearView: false;
      startDateObj: Date;
      endDateObj: Date;
      isMoreThanHalfYear: boolean;
      quarters: TimelineDateAxisQuarter[];
    }
  | {
      view: 'month';
      useQuarterView: false;
      useYearView: false;
      startDateObj: Date;
      endDateObj: Date;
      isMoreThanHalfYear: boolean;
      months: TimelineDateAxisMonthGroup[];
    }
  | {
      view: 'year';
      useQuarterView: false;
      useYearView: true;
      startDateObj: Date;
      endDateObj: Date;
      isMoreThanHalfYear: boolean;
      years: TimelineDateAxisYear[];
    };

// Clamps each range's edges to the scope and drops ranges that fall entirely
// outside it. Shared by the quarter (month ranges) and month (bi-week ranges)
// paths of buildTimelineDateAxis so both trim to scope identically.
const clampRangesToScope = (
  ranges: { start: Date; end: Date }[],
  scopeStart: Date,
  scopeEnd: Date,
): { start: Date; end: Date }[] =>
  ranges
    .map((range) => ({
      start: range.start < scopeStart ? scopeStart : range.start,
      end: range.end > scopeEnd ? scopeEnd : range.end,
    }))
    .filter((range) => range.end >= scopeStart && range.start <= scopeEnd);

/**
 * Builds the canonical date geometry used by both timeline column rendering and
 * item bucketing. Column callers walk the structure to produce columns with
 * width math; data callers flatten it via {@link flattenTimelineDateAxisToRanges}
 * to bucket items into the same keys. Keeping both paths anchored on this builder
 * guarantees that column accessors and item bucket keys stay aligned.
 */
export const buildTimelineDateAxis = ({
  iterationFrom,
  iterationTo,
  displayFrom,
  displayTo,
  granularity,
}: {
  iterationFrom: string;
  iterationTo: string;
  displayFrom?: string;
  displayTo?: string;
  granularity?: TimelineGranularity;
}): TimelineDateAxis | null => {
  const from = displayFrom ?? iterationFrom;
  const to = displayTo ?? iterationTo;
  const startDateObj = fromDateString(iterationFrom);
  const endDateObj = fromDateString(iterationTo);
  if (!startDateObj || !endDateObj) return null;

  const daysInScope = differenceInDays(endDateObj, startDateObj) + 1;
  const isMoreThanHalfYear = daysInScope > 182;
  const resolvedGranularity: TimelineGranularity =
    granularity ?? (shouldUseQuarterView(from, to) ? 'quarter' : 'month');

  if (resolvedGranularity === 'year') {
    const yearsById = new Map<number, TimelineDateAxisYear>();
    for (const quarterDate of eachQuarterOfInterval({ start: startDateObj, end: endDateObj })) {
      const year = quarterDate.getUTCFullYear();
      const quarter = getQuarter(quarterDate);
      const quarterStart = toUTCMidnight(year, (quarter - 1) * 3, 1);
      const quarterEnd = toUTCMidnight(year, quarter * 3, 0);
      const [clamped] = clampRangesToScope(
        [{ start: quarterStart, end: quarterEnd }],
        startDateObj,
        endDateObj,
      );
      if (!clamped) continue;
      let yearEntry = yearsById.get(year);
      if (!yearEntry) {
        yearEntry = { year, quarters: [] };
        yearsById.set(year, yearEntry);
      }
      yearEntry.quarters.push({
        start: clamped.start,
        end: clamped.end,
        // Key off the canonical (unclamped) quarter start so the bucket key is
        // identical across the data and column hooks, which build the axis from
        // different scope boundaries. Clamped start is used only for geometry.
        key: format(quarterStart, 'yyyy-MM-dd'),
        year,
        quarter,
        quarterLabel: getQuarterLabel(quarter, year),
        indexInYear: yearEntry.quarters.length,
      });
    }
    return {
      view: 'year',
      useQuarterView: false,
      useYearView: true,
      startDateObj,
      endDateObj,
      isMoreThanHalfYear,
      years: Array.from(yearsById.values()).filter((y) => y.quarters.length > 0),
    };
  }

  if (resolvedGranularity === 'quarter') {
    const quarters: TimelineDateAxisQuarter[] = [];
    for (const quarterDate of eachQuarterOfInterval({ start: startDateObj, end: endDateObj })) {
      const year = quarterDate.getUTCFullYear();
      const quarter = getQuarter(quarterDate);
      const monthRanges = getTimelineColumnRangesInQuarter({ year, quarter, from, to });
      const monthsInScope = clampRangesToScope(monthRanges, startDateObj, endDateObj);
      const months: TimelineDateAxisMonth[] = monthsInScope.map((m, indexInQuarter) => ({
        start: m.start,
        end: m.end,
        key: format(m.start, 'yyyy-MM-dd'),
        year: m.start.getUTCFullYear(),
        monthIndex: m.start.getUTCMonth(),
        indexInQuarter,
      }));
      if (months.length > 0) {
        quarters.push({
          year,
          quarter,
          quarterLabel: getQuarterLabel(quarter, year),
          months,
        });
      }
    }
    return {
      view: 'quarter',
      useQuarterView: true,
      useYearView: false,
      startDateObj,
      endDateObj,
      isMoreThanHalfYear,
      quarters,
    };
  }

  const months: TimelineDateAxisMonthGroup[] = [];
  const firstMonthStart = toUTCMidnight(
    startDateObj.getUTCFullYear(),
    startDateObj.getUTCMonth(),
    1,
  );
  for (
    let cursor = firstMonthStart;
    cursor <= endDateObj;
    cursor = toUTCMidnight(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1)
  ) {
    const year = cursor.getUTCFullYear();
    const monthIndex = cursor.getUTCMonth();
    const calendarMonthStart = cursor;
    const calendarMonthEnd = toUTCMidnight(year, monthIndex + 1, 0);
    const monthName = getMonthName(calendarMonthStart);
    const visibleMonthStart = isBefore(startDateObj, calendarMonthStart)
      ? calendarMonthStart
      : startDateObj;
    const visibleMonthEnd = isBefore(calendarMonthEnd, endDateObj) ? calendarMonthEnd : endDateObj;

    const biWeekRanges = getTwoWeekRangesInMonth({ year, month: monthIndex, from, to });
    const biWeeksInScope = clampRangesToScope(biWeekRanges, startDateObj, endDateObj);

    const biWeeks: TimelineDateAxisBiWeek[] = biWeeksInScope.map((b, index) => ({
      start: b.start,
      end: b.end,
      key: format(b.start, 'yyyy-MM-dd'),
      index,
    }));

    months.push({
      year,
      monthIndex,
      monthName,
      visibleMonthStart,
      visibleMonthEnd,
      biWeeks,
      isEmptyState: biWeeks.length === 0,
    });
  }

  return {
    view: 'month',
    useQuarterView: false,
    useYearView: false,
    startDateObj,
    endDateObj,
    isMoreThanHalfYear,
    months,
  };
};

/**
 * Flattens a {@link TimelineDateAxis} to the list of `{ start, end, key }`
 * ranges used for bucketing items. Keys here match the column accessor keys
 * produced from the same axis, so an item bucketed into `key` ends up rendered
 * in the column that uses `accessor("items." + key)`.
 */
export const flattenTimelineDateAxisToRanges = (
  axis: TimelineDateAxis,
): { start: Date; end: Date; key: string }[] => {
  if (axis.view === 'year') {
    const out: { start: Date; end: Date; key: string }[] = [];
    for (const year of axis.years) {
      for (const quarter of year.quarters) {
        out.push({ start: quarter.start, end: quarter.end, key: quarter.key });
      }
    }
    return out;
  }
  if (axis.useQuarterView) {
    const out: { start: Date; end: Date; key: string }[] = [];
    for (const quarter of axis.quarters) {
      for (const month of quarter.months) {
        out.push({ start: month.start, end: month.end, key: month.key });
      }
    }
    return out;
  }
  const out: { start: Date; end: Date; key: string }[] = [];
  for (const month of axis.months) {
    for (const biWeek of month.biWeeks) {
      out.push({ start: biWeek.start, end: biWeek.end, key: biWeek.key });
    }
  }
  return out;
};

export const getTimelineColumnRangesInQuarter = ({
  year,
  quarter,
  from,
  to,
}: {
  year: number;
  quarter: number;
  from: string;
  to: string;
}) => {
  const startMonth = (quarter - 1) * 3;
  const endMonth = startMonth + 2;

  const fromDate = dateToUTCMidnight(parse(from, 'yyyy-MM-dd', new Date()));
  const toDate = dateToUTCMidnight(parse(to, 'yyyy-MM-dd', new Date()));

  const months: { start: Date; end: Date }[] = [];

  for (let monthIndex = startMonth; monthIndex <= endMonth; monthIndex++) {
    const utcStart = toUTCMidnight(year, monthIndex, 1);
    const utcEnd = toUTCMidnight(year, monthIndex + 1, 0);
    if (utcEnd < fromDate || utcStart > toDate) {
      continue;
    }

    let rangeStart = utcStart;
    let rangeEnd = utcEnd;

    if (rangeStart < fromDate) {
      rangeStart = fromDate;
    }

    if (rangeEnd > toDate) {
      rangeEnd = toDate;
    }

    months.push({
      start: rangeStart,
      end: rangeEnd,
    });
  }

  return months;
};
